import { EntryEditError } from '../edits.ts';
import { javaIsBlank } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlRow } from '../sql.ts';

/**
 * The Game Master Toolkit's Prep (docs/PARITY-STEP-5-PLAN.md section 7), read and written as
 * the desktop's `CampaignRepository` and `CampaignService` do:
 * - campaigns by name, without regard to case, with their session, encounter and quest
 *   counts; a campaign's party (player characters) and cast are entries of the realm;
 * - sessions by number (a new one is numbered after the ones there are), encounters and
 *   quests by when they were made;
 * - a blank name is "Untitled Campaign", "Untitled Session", "Untitled Encounter" or
 *   "Untitled Quest";
 * - deleting a campaign deletes its sessions, encounters (with their combatants) and quests
 *   (the schema's cascades); deleting a session keeps its encounters, with no session.
 */

export const QUEST_STATUSES = ['ACTIVE', 'COMPLETED', 'FAILED'] as const;
export type QuestStatus = (typeof QUEST_STATUSES)[number];
export const ENCOUNTER_DIFFICULTIES = ['TRIVIAL', 'EASY', 'MEDIUM', 'HARD', 'DEADLY'] as const;
export type EncounterDifficulty = (typeof ENCOUNTER_DIFFICULTIES)[number];

export interface CampaignSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly sessionCount: number;
  readonly encounterCount: number;
  readonly questCount: number;
  readonly created: string;
  readonly modified: string;
}

export interface Campaign {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly notes: string;
  readonly playerCharacterIds: readonly string[];
  readonly castIds: readonly string[];
  /** The session the Run screen's notes and play log use; null for none. */
  readonly currentSessionId: string | null;
  /** The encounter being run; null for none. */
  readonly activeEncounterId: string | null;
  readonly created: string;
  readonly modified: string;
}

export interface CampaignSession {
  readonly id: string;
  readonly campaignId: string;
  readonly sessionNumber: number;
  readonly title: string;
  readonly summary: string;
  readonly notes: string;
  /** Lines written at the table, "[20:41] Warlord Kessian takes 7 (58/65).", editable like the notes. */
  readonly playLog: string;
  readonly playedOn: string;
  readonly created: string;
  readonly modified: string;
}

/** An encounter as Prep lists it: its details, how many are in it, and whether it's being run. */
export interface EncounterSummary {
  readonly id: string;
  readonly campaignId: string;
  readonly sessionId: string | null;
  readonly name: string;
  readonly description: string;
  readonly difficulty: EncounterDifficulty;
  /** 0 until it's started; then the round it's in. */
  readonly round: number;
  readonly combatantCount: number;
  readonly created: string;
  readonly modified: string;
}

export interface Quest {
  readonly id: string;
  readonly campaignId: string;
  readonly title: string;
  readonly description: string;
  readonly status: QuestStatus;
  readonly entryIds: readonly string[];
  readonly created: string;
  readonly modified: string;
}

export const CAMPAIGN_LIMITS = { name: 200, text: 20_000, playLog: 500_000 } as const;

// ---- reading ----

export function listCampaigns(conn: SqlConnection): CampaignSummary[] {
  return conn
    .all(
      `SELECT c.id, c.name, c.description, c.created, c.modified,
         (SELECT COUNT(*) FROM campaign_sessions s WHERE s.campaign_id = c.id) AS session_count,
         (SELECT COUNT(*) FROM campaign_encounters e WHERE e.campaign_id = c.id) AS encounter_count,
         (SELECT COUNT(*) FROM campaign_quests q WHERE q.campaign_id = c.id) AS quest_count
       FROM campaigns c ORDER BY c.name COLLATE NOCASE`,
    )
    .map((row) => ({
      id: text(row['id']),
      name: text(row['name']),
      description: text(row['description']),
      sessionCount: int(row['session_count']),
      encounterCount: int(row['encounter_count']),
      questCount: int(row['quest_count']),
      created: text(row['created']),
      modified: text(row['modified']),
    }));
}

export function getCampaign(conn: SqlConnection, id: string): Campaign | null {
  const row = conn.all('SELECT id, name, description, notes, current_session_id, active_encounter_id, created, modified FROM campaigns WHERE id = ?', [id])[0];
  if (!row) return null;
  return {
    id,
    name: text(row['name']),
    description: text(row['description']),
    notes: text(row['notes']),
    playerCharacterIds: joined(conn, 'campaign_player_characters', 'campaign_id', id),
    castIds: joined(conn, 'campaign_cast', 'campaign_id', id),
    currentSessionId: optional(row['current_session_id']),
    activeEncounterId: optional(row['active_encounter_id']),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

export function listSessions(conn: SqlConnection, campaignId: string): CampaignSession[] {
  return conn
    .all('SELECT id, campaign_id, session_number, title, summary, notes, play_log, played_on, created, modified FROM campaign_sessions WHERE campaign_id = ? ORDER BY session_number', [campaignId])
    .map(sessionOf);
}

export function listEncounters(conn: SqlConnection, campaignId: string): EncounterSummary[] {
  return conn
    .all(
      `SELECT id, campaign_id, session_id, name, description, difficulty, round, created, modified,
         (SELECT COUNT(*) FROM encounter_combatants c WHERE c.encounter_id = e.id) AS combatant_count
       FROM campaign_encounters e WHERE campaign_id = ? ORDER BY created`,
      [campaignId],
    )
    .map((row) => ({
      id: text(row['id']),
      campaignId: text(row['campaign_id']),
      sessionId: optional(row['session_id']),
      name: text(row['name']),
      description: text(row['description']),
      difficulty: difficultyOf(text(row['difficulty'])),
      round: int(row['round']),
      combatantCount: int(row['combatant_count']),
      created: text(row['created']),
      modified: text(row['modified']),
    }));
}

export function listQuests(conn: SqlConnection, campaignId: string): Quest[] {
  return conn.all('SELECT id, campaign_id, title, description, status, created, modified FROM campaign_quests WHERE campaign_id = ? ORDER BY created', [campaignId]).map((row) => {
    const id = text(row['id']);
    const status = text(row['status']);
    return {
      id,
      campaignId: text(row['campaign_id']),
      title: text(row['title']),
      description: text(row['description']),
      status: (QUEST_STATUSES as readonly string[]).includes(status) ? (status as QuestStatus) : 'ACTIVE',
      entryIds: joined(conn, 'quest_entries', 'quest_id', id),
      created: text(row['created']),
      modified: text(row['modified']),
    };
  });
}

// ---- campaigns ----

export interface CampaignChanges {
  readonly name?: string;
  readonly description?: string;
  readonly notes?: string;
  readonly playerCharacterIds?: readonly string[];
  readonly castIds?: readonly string[];
}

export function createCampaign(conn: SqlConnection, name: string, description: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = { name: cleanName(name, 'Untitled Campaign'), description: cleanText(description, 'A description') };
  conn.run("INSERT INTO campaigns (name, description, notes, current_session_id, active_encounter_id, created, modified, id) VALUES (?, ?, '', NULL, NULL, ?, ?, ?)", [
    clean.name,
    clean.description,
    now,
    now,
    id,
  ]);
  return id;
}

export function updateCampaign(conn: SqlConnection, id: string, changes: CampaignChanges, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    mustExist(conn, 'campaigns', id, 'That campaign no longer exists.');
    const set: string[] = [];
    const params: string[] = [];
    const column = (name: string, value: string) => {
      set.push(`${name} = ?`);
      params.push(value);
    };
    if (changes.name !== undefined) column('name', cleanName(changes.name, 'Untitled Campaign'));
    if (changes.description !== undefined) column('description', cleanText(changes.description, 'A description'));
    if (changes.notes !== undefined) column('notes', cleanText(changes.notes, 'Notes'));
    conn.run(`UPDATE campaigns SET ${[...set, 'modified = ?'].join(', ')} WHERE id = ?`, [...params, now, id]);
    if (changes.playerCharacterIds !== undefined) replaceEntries(conn, 'campaign_player_characters', 'campaign_id', id, changes.playerCharacterIds);
    if (changes.castIds !== undefined) replaceEntries(conn, 'campaign_cast', 'campaign_id', id, changes.castIds);
  });
}

/** The session the play log writes to (`setTable`), keeping the encounter being run. Null for none. */
export function setCurrentSession(conn: SqlConnection, campaignId: string, sessionId: string | null, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    mustExist(conn, 'campaigns', campaignId, 'That campaign no longer exists.');
    if (sessionId !== null && int(scalar(conn, 'SELECT COUNT(*) FROM campaign_sessions WHERE id = ? AND campaign_id = ?', [sessionId, campaignId])) === 0) {
      throw new EntryEditError('not_found', 'That session no longer exists.');
    }
    conn.run('UPDATE campaigns SET current_session_id = ?, modified = ? WHERE id = ?', [sessionId, now, campaignId]);
  });
}

/** Deletes the campaign with its sessions, encounters and quests. */
export function deleteCampaign(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'campaigns', id, 'That campaign no longer exists.');
    conn.run('DELETE FROM campaigns WHERE id = ?', [id]);
  });
}

// ---- sessions ----

export interface SessionChanges {
  readonly title?: string;
  readonly summary?: string;
  readonly notes?: string;
  readonly playLog?: string;
  /** An ISO time: the day it was played. */
  readonly playedOn?: string;
}

/** The next session: numbered after the ones there are, "Session N" unless named, played today. */
export function createSession(conn: SqlConnection, campaignId: string, title: string | null = null, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  transaction(conn, () => {
    mustExist(conn, 'campaigns', campaignId, 'That campaign no longer exists.');
    const number = int(scalar(conn, 'SELECT COUNT(*) FROM campaign_sessions WHERE campaign_id = ?', [campaignId])) + 1;
    conn.run(
      "INSERT INTO campaign_sessions (title, summary, notes, play_log, played_on, created, modified, campaign_id, session_number, id) VALUES (?, '', '', '', ?, ?, ?, ?, ?, ?)",
      [cleanName(title ?? `Session ${number}`, 'Untitled Session'), now, now, now, campaignId, number, id],
    );
  });
  return id;
}

export function updateSession(conn: SqlConnection, id: string, changes: SessionChanges, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    mustExist(conn, 'campaign_sessions', id, 'That session no longer exists.');
    const set: string[] = [];
    const params: string[] = [];
    const column = (name: string, value: string) => {
      set.push(`${name} = ?`);
      params.push(value);
    };
    if (changes.title !== undefined) column('title', cleanName(changes.title, 'Untitled Session'));
    if (changes.summary !== undefined) column('summary', cleanText(changes.summary, 'A summary'));
    if (changes.notes !== undefined) column('notes', cleanText(changes.notes, 'Notes'));
    if (changes.playLog !== undefined) {
      if (changes.playLog.length > CAMPAIGN_LIMITS.playLog) throw new EntryEditError('invalid', 'The play log is too long to save.');
      column('play_log', changes.playLog);
    }
    if (changes.playedOn !== undefined) {
      if (Number.isNaN(Date.parse(changes.playedOn))) throw new EntryEditError('invalid', 'The day played must be a date.');
      column('played_on', new Date(changes.playedOn).toISOString());
    }
    conn.run(`UPDATE campaign_sessions SET ${[...set, 'modified = ?'].join(', ')} WHERE id = ?`, [...params, now, id]);
  });
}

/** Deletes the session; its encounters stay, with no session. */
export function deleteSession(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'campaign_sessions', id, 'That session no longer exists.');
    conn.run('DELETE FROM campaign_sessions WHERE id = ?', [id]);
  });
}

// ---- encounters ----

export interface EncounterDetails {
  readonly name: string;
  readonly description: string;
  readonly difficulty: EncounterDifficulty;
  readonly sessionId: string | null;
}

export function createEncounter(conn: SqlConnection, campaignId: string, details: EncounterDetails, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  transaction(conn, () => {
    mustExist(conn, 'campaigns', campaignId, 'That campaign no longer exists.');
    const clean = cleanEncounter(conn, campaignId, details);
    conn.run(
      'INSERT INTO campaign_encounters (campaign_id, session_id, name, description, difficulty, round, current_combatant_id, created, modified, id) VALUES (?, ?, ?, ?, ?, 0, NULL, ?, ?, ?)',
      [campaignId, clean.sessionId, clean.name, clean.description, clean.difficulty, now, now, id],
    );
  });
  return id;
}

export function updateEncounterDetails(conn: SqlConnection, id: string, details: EncounterDetails, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const campaignId = scalar(conn, 'SELECT campaign_id FROM campaign_encounters WHERE id = ?', [id]);
    if (campaignId === null) throw new EntryEditError('not_found', 'That encounter no longer exists.');
    const clean = cleanEncounter(conn, text(campaignId), details);
    conn.run('UPDATE campaign_encounters SET session_id = ?, name = ?, description = ?, difficulty = ?, modified = ? WHERE id = ?', [
      clean.sessionId,
      clean.name,
      clean.description,
      clean.difficulty,
      now,
      id,
    ]);
  });
}

/** Deletes the encounter with its combatants. */
export function deleteEncounter(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'campaign_encounters', id, 'That encounter no longer exists.');
    conn.run('DELETE FROM encounter_combatants WHERE encounter_id = ?', [id]);
    conn.run('DELETE FROM campaign_encounters WHERE id = ?', [id]);
  });
}

// ---- quests ----

export interface QuestChanges {
  readonly title?: string;
  readonly description?: string;
  readonly status?: QuestStatus;
  readonly entryIds?: readonly string[];
}

export function createQuest(conn: SqlConnection, campaignId: string, title = 'New Quest', id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  transaction(conn, () => {
    mustExist(conn, 'campaigns', campaignId, 'That campaign no longer exists.');
    conn.run("INSERT INTO campaign_quests (title, description, status, created, modified, campaign_id, id) VALUES (?, '', 'ACTIVE', ?, ?, ?, ?)", [
      cleanName(title, 'Untitled Quest'),
      now,
      now,
      campaignId,
      id,
    ]);
  });
  return id;
}

export function updateQuest(conn: SqlConnection, id: string, changes: QuestChanges, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    mustExist(conn, 'campaign_quests', id, 'That quest no longer exists.');
    const set: string[] = [];
    const params: string[] = [];
    const column = (name: string, value: string) => {
      set.push(`${name} = ?`);
      params.push(value);
    };
    if (changes.title !== undefined) column('title', cleanName(changes.title, 'Untitled Quest'));
    if (changes.description !== undefined) column('description', cleanText(changes.description, 'A description'));
    if (changes.status !== undefined) {
      if (!QUEST_STATUSES.includes(changes.status)) throw new EntryEditError('invalid', 'That is not a quest status.');
      column('status', changes.status);
    }
    conn.run(`UPDATE campaign_quests SET ${[...set, 'modified = ?'].join(', ')} WHERE id = ?`, [...params, now, id]);
    if (changes.entryIds !== undefined) replaceEntries(conn, 'quest_entries', 'quest_id', id, changes.entryIds);
  });
}

export function deleteQuest(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'campaign_quests', id, 'That quest no longer exists.');
    conn.run('DELETE FROM campaign_quests WHERE id = ?', [id]);
  });
}

// ---- rows and checks ----

function sessionOf(row: SqlRow): CampaignSession {
  return {
    id: text(row['id']),
    campaignId: text(row['campaign_id']),
    sessionNumber: int(row['session_number']),
    title: text(row['title']),
    summary: text(row['summary']),
    notes: text(row['notes']),
    playLog: text(row['play_log']),
    playedOn: text(row['played_on']),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

const optional = (value: unknown) => (value === null || value === undefined || javaIsBlank(String(value)) ? null : String(value));
const difficultyOf = (value: string): EncounterDifficulty => ((ENCOUNTER_DIFFICULTIES as readonly string[]).includes(value) ? (value as EncounterDifficulty) : 'MEDIUM');

/** `table` and its columns are always this module's literals, never input. */
function joined(conn: SqlConnection, table: string, owner: string, id: string): string[] {
  return conn.all(`SELECT entry_id FROM ${table} WHERE ${owner} = ? ORDER BY rowid`, [id]).map((r) => text(r['entry_id']));
}

/** As the desktop's `replaceJoinRows`: the whole set, each entry checked to exist. */
function replaceEntries(conn: SqlConnection, table: string, owner: string, id: string, entryIds: readonly string[]): void {
  const ids = [...new Set(entryIds)];
  for (const entryId of ids) mustExist(conn, 'lore_entries', entryId, 'One of the chosen entries no longer exists.');
  conn.run(`DELETE FROM ${table} WHERE ${owner} = ?`, [id]);
  for (const entryId of ids) conn.run(`INSERT INTO ${table} (${owner}, entry_id) VALUES (?, ?)`, [id, entryId]);
}

function cleanEncounter(conn: SqlConnection, campaignId: string, details: EncounterDetails) {
  if (!ENCOUNTER_DIFFICULTIES.includes(details.difficulty)) throw new EntryEditError('invalid', 'That is not an encounter difficulty.');
  if (details.sessionId !== null && int(scalar(conn, 'SELECT COUNT(*) FROM campaign_sessions WHERE id = ? AND campaign_id = ?', [details.sessionId, campaignId])) === 0) {
    throw new EntryEditError('not_found', 'That session is not one of this campaign’s any more.');
  }
  return { name: cleanName(details.name, 'Untitled Encounter'), description: cleanText(details.description, 'A description'), difficulty: details.difficulty, sessionId: details.sessionId };
}

/** As the desktop's constructors: a blank name becomes the default, otherwise it's kept as written (on one line). */
function cleanName(name: string, fallback: string): string {
  if (name.length > CAMPAIGN_LIMITS.name) throw new EntryEditError('invalid', `A name can be at most ${CAMPAIGN_LIMITS.name} characters.`);
  return javaIsBlank(name) ? fallback : name.replace(/[\r\n]+/g, ' ');
}

function cleanText(value: string, what: string): string {
  if (value.length > CAMPAIGN_LIMITS.text) throw new EntryEditError('invalid', `${what} can be at most ${CAMPAIGN_LIMITS.text.toLocaleString('en')} characters.`);
  return value;
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
